/**
 * Service Worker Registration Utility
 * Registers and manages the service worker for offline capability
 */

let registration = null;
let isServiceWorkerSupported = 'serviceWorker' in navigator;

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration or null
 */
export const registerServiceWorker = async () => {
  if (!isServiceWorkerSupported) {
    console.log('[Service Worker] Not supported in this browser');
    return null;
  }

  // Only register in production build (not in development)
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Service Worker] Skipping registration in development mode');
    return null;
  }

  try {
    // Check if service worker is already registered
    registration = await navigator.serviceWorker.getRegistration();
    
    if (registration) {
      console.log('[Service Worker] Already registered');
      return registration;
    }

    // Register new service worker with a cache-busting timestamp to ensure the script itself is fresh
    const swUrl = `/service-worker.js?v=${Date.now()}`;
    registration = await navigator.serviceWorker.register(swUrl, {
      scope: '/'
    });

    console.log('[Service Worker] Registered successfully:', registration.scope);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker is available
          console.log('[Service Worker] New version available');
          
          // Optional: Show notification to user about new version
          if (window.confirm('A new version is available. Reload to update?')) {
            newWorker.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
          }
        }
      });
    });

    // Handle service worker messages
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        console.log('[Service Worker] Updated, reloading page');
        window.location.reload();
      }
    });

    // Check for updates periodically
    setInterval(() => {
      if (registration) {
        registration.update();
      }
    }, 60000); // Check every minute

    return registration;
  } catch (error) {
    console.error('[Service Worker] Registration failed:', error);
    return null;
  }
};

/**
 * Unregister the service worker
 * Useful for development or when disabling offline capability
 */
export const unregisterServiceWorker = async () => {
  if (!isServiceWorkerSupported) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const success = await registration.unregister();
      if (success) {
        console.log('[Service Worker] Unregistered successfully');
      }
    }
  } catch (error) {
    console.error('[Service Worker] Unregistration failed:', error);
  }
};

/**
 * Check if service worker is active
 * @returns {Promise<boolean>} True if service worker is active
 */
export const isServiceWorkerActive = async () => {
  if (!isServiceWorkerSupported) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return !!registration && !!navigator.serviceWorker.controller;
  } catch (error) {
    console.error('[Service Worker] Error checking status:', error);
    return false;
  }
};

/**
 * Get service worker registration
 * @returns {Promise<ServiceWorkerRegistration|null>} Service worker registration or null
 */
export const getServiceWorkerRegistration = async () => {
  if (!isServiceWorkerSupported) {
    return null;
  }

  try {
    return await navigator.serviceWorker.getRegistration();
  } catch (error) {
    console.error('[Service Worker] Error getting registration:', error);
    return null;
  }
};

/**
 * Check if app is online
 * @returns {boolean} True if online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Add online/offline event listeners
 * @param {Function} onOnline - Callback when online
 * @param {Function} onOffline - Callback when offline
 * @returns {Function} Cleanup function to remove listeners
 */
export const addOnlineStatusListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

export default {
  register: registerServiceWorker,
  unregister: unregisterServiceWorker,
  isActive: isServiceWorkerActive,
  getRegistration: getServiceWorkerRegistration,
  isOnline,
  addOnlineStatusListeners
};

